import { useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger } from "@/components/ui/select"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ToMarkdown } from "@/components/ui/tomarkdown"
import { cn } from "@/lib/utils"
import { AllAIModels } from "@/lib/agents/modellist"
import {
  Sparkles, X, History, Plus, MoreHorizontal, ArrowUp, Square, Trash2, Zap, Brain, FolderUp, ShieldAlert, Loader2, ImagePlus,
} from "lucide-react"
import { ToolExecutionList } from "@/pages/dev/smart-cms/shared/components/ToolExecutionList"
import { CorpusPanel } from "@/pages/dev/smart-cms/shared/components/CorpusPanel"
import { RETENTION_DAYS } from "@/lib/assistant/store"
import { applicableSkills } from "@/lib/assistant/prompt"
import { interleave } from "@/lib/assistant/interleave"
import { MAX_IMAGES, readImage } from "@/lib/assistant/images"
import { useAssistant, type PendingApproval } from "./AssistantProvider"
import { SkillsDialog, MemoryDialog } from "./AssistantSettings"
import { QuestionCard } from "./QuestionCard"

// cms:<id> links open the article in the Smart CMS editor
function withAppLinks(markdown: string): string {
  return markdown.replace(/\]\(cms:([^)\s]+)\)/g, (_, id) => `](/smart-cms?open=articles/node:${encodeURIComponent(id)})`)
}

// Starter prompts for what's open right now
function suggestionsFor(moduleIds: string[]): string[] {
  const out: string[] = []
  if (moduleIds.includes("cms.serviceMap")) out.push("Which services are missing a Spanish translation?")
  if (moduleIds.includes("cms.articles")) out.push("List draft articles that haven't been reviewed")
  if (moduleIds.includes("cms.siteConfig")) out.push("Summarise this site's header navigation")
  if (moduleIds.includes("flow.editor")) out.push("Explain what this agent does, step by step")
  if (moduleIds.includes("dashboard")) out.push("Summarise this period's escalations")
  if (moduleIds.includes("forms")) out.push("Create a short satisfaction survey form")
  out.push("What can you help me with on this page?")
  return out.slice(0, 4)
}

const WIDTH_KEY = "assistant-width"
const DEFAULT_WIDTH = 400
const clampWidth = (w: number) => Math.round(Math.min(Math.max(w, 320), Math.max(480, window.innerWidth * 0.7)))

// "OpenAI - gpt-5.6-luna" → provider and model name
function splitLabel(label: string) {
  const i = label.indexOf(" - ")
  return i < 0 ? { provider: "Other", name: label } : { provider: label.slice(0, i), name: label.slice(i + 3) }
}

const MODEL_GROUPS = (() => {
  const groups = new Map<string, { value: string; name: string }[]>()
  for (const m of AllAIModels) {
    const { provider, name } = splitLabel(m.label)
    groups.set(provider, [...(groups.get(provider) ?? []), { value: m.value, name }])
  }
  return [...groups.entries()]
})()

// Compact picker for the header: the model name, with the full list grouped by provider
function ModelPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const current = AllAIModels.find(m => m.value === value)
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        title={`Model: ${current?.label ?? value}`}
        className="h-7 w-auto max-w-[150px] shrink-0 gap-1 rounded-md border-0 bg-transparent px-2 text-[11px] font-medium text-muted-foreground shadow-none hover:bg-muted hover:text-foreground focus:ring-0 focus:ring-offset-0"
      >
        <span className="truncate">{current ? splitLabel(current.label).name : value}</span>
      </SelectTrigger>
      <SelectContent align="end" className="max-h-80">
        {MODEL_GROUPS.map(([provider, models]) => (
          <SelectGroup key={provider}>
            <SelectLabel className="pl-2 text-[11px] font-medium text-muted-foreground">{provider}</SelectLabel>
            {models.map(m => <SelectItem key={m.value} value={m.value} className="text-xs">{m.name}</SelectItem>)}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  )
}

function ApprovalCard({ approval, onResolve }: { approval: PendingApproval; onResolve: (value: any) => void }) {
  if (approval.kind === "question" && approval.question) {
    return <QuestionCard key={approval.id} question={approval.question} onAnswer={onResolve} />
  }
  if (approval.kind === "custom" && approval.render) {
    return <div className="border-t bg-background">{approval.render(onResolve)}</div>
  }
  const req = approval.req!
  return (
    <div className="space-y-2 border-t bg-amber-50/60 p-3 dark:bg-amber-950/20">
      <div className="flex items-start gap-2">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
        <p className="text-sm font-medium">{req.summary}</p>
      </div>
      {(req.before || req.after) && (
        <div className="grid gap-1 text-xs">
          {req.before && <pre className="max-h-32 overflow-auto whitespace-pre-wrap rounded bg-red-50 p-2 dark:bg-red-950/30">{req.before}</pre>}
          {req.after && <pre className="max-h-32 overflow-auto whitespace-pre-wrap rounded bg-emerald-50 p-2 dark:bg-emerald-950/30">{req.after}</pre>}
        </div>
      )}
      {req.detail && (
        <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-[11px]">{req.detail}</pre>
      )}
      <div className="flex gap-2">
        <Button size="sm" className="h-7 text-xs" onClick={() => onResolve(true)}>Approve</Button>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onResolve(false)}>Reject</Button>
      </div>
    </div>
  )
}

export function AssistantPanel() {
  const a = useAssistant()
  const navigate = useNavigate()
  const [view, setView] = useState<"chat" | "history">("chat")
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [skillsOpen, setSkillsOpen] = useState(false)
  const [memoryOpen, setMemoryOpen] = useState(false)
  const [docsOpen, setDocsOpen] = useState(false)
  const [unread, setUnread] = useState(0)
  // Screenshots waiting to go with the next message
  const [images, setImages] = useState<string[]>([])
  const [imageError, setImageError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [viewing, setViewing] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const addImages = async (files: File[]) => {
    const picked = files.filter(f => f.type.startsWith("image/"))
    if (!picked.length) return
    setImageError(null)
    const room = MAX_IMAGES - images.length
    if (room <= 0) { setImageError(`Up to ${MAX_IMAGES} screenshots per message.`); return }
    try {
      const read = await Promise.all(picked.slice(0, room).map(readImage))
      setImages(prev => [...prev, ...read].slice(0, MAX_IMAGES))
      if (picked.length > room) setImageError(`Up to ${MAX_IMAGES} screenshots per message; the rest were left out.`)
    } catch (e: any) {
      setImageError(e?.message || "Couldn't read that image.")
    }
    inputRef.current?.focus()
  }
  const [width, setWidth] = useState(() => {
    try { return clampWidth(Number(localStorage.getItem(WIDTH_KEY)) || DEFAULT_WIDTH) } catch { return DEFAULT_WIDTH }
  })
  const [resizing, setResizing] = useState(false)

  // Drag the left edge to resize; double-click resets
  const startResize = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = width
    let next = startWidth
    setResizing(true)
    const onMove = (ev: globalThis.MouseEvent) => {
      next = clampWidth(startWidth + (startX - ev.clientX))
      setWidth(next)
    }
    const onUp = () => {
      setResizing(false)
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
      try { localStorage.setItem(WIDTH_KEY, String(next)) } catch { /* private window */ }
    }
    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
  }
  const resetWidth = () => {
    setWidth(DEFAULT_WIDTH)
    try { localStorage.setItem(WIDTH_KEY, String(DEFAULT_WIDTH)) } catch { /* private window */ }
  }
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const moduleIds = a.openModules.map(m => m.id)
  const slashSkills = useMemo(
    () => applicableSkills(a.skills, moduleIds).filter(s => s.trigger === "slash"),
    [a.skills, moduleIds.join(",")], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const slashQuery = /^\/\S*$/.test(a.draft) ? a.draft.slice(1).toLowerCase() : null
  const slashMatches = slashQuery === null ? [] : slashSkills.filter(s => s.name.toLowerCase().startsWith(slashQuery))

  // Keep the newest message in view
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const el = scrollRef.current
      if (el) el.scrollTop = el.scrollHeight
    })
    return () => cancelAnimationFrame(frame)
  }, [a.messages, a.approvals.length])

  // Replies that finish while the panel is closed get a badge on the launcher
  const lastCount = useRef(a.messages.length)
  useEffect(() => {
    if (!a.open && a.messages.length > lastCount.current && !a.busy) setUnread(n => n + 1)
    lastCount.current = a.messages.length
  }, [a.busy]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (a.open) setUnread(0) }, [a.open])

  useEffect(() => { if (a.open) inputRef.current?.focus() }, [a.open])

  // The input grows with its text, up to a few lines, then scrolls
  useLayoutEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }, [a.draft, a.open])

  if (!a.enabled) return null

  if (!a.open) {
    return (
      <button
        type="button"
        onClick={() => a.setOpen(true)}
        title="Open the assistant"
        className="fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        {a.busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
            {unread}
          </span>
        )}
      </button>
    )
  }

  // App links navigate in place; other links open in a new tab
  const onLinkClick = (e: MouseEvent<HTMLDivElement>) => {
    const link = (e.target as HTMLElement).closest("a")
    const href = link?.getAttribute("href")
    if (!href) return
    e.preventDefault()
    if (href.startsWith("/")) navigate(href)
    else if (/^https?:/i.test(href)) window.open(href, "_blank", "noopener,noreferrer")
  }

  // While the assistant is waiting on a question, what's typed is the answer
  const pendingQuestion = a.approvals[0]?.kind === "question" ? a.approvals[0] : null

  const submit = () => {
    if (pendingQuestion && a.draft.trim()) {
      a.resolveApproval(pendingQuestion.id, { choices: [], other: a.draft.trim() })
      a.setDraft("")
      return
    }
    if ((!a.draft.trim() && !images.length) || a.busy) return
    const text = a.draft
    const shots = images
    a.setDraft("")
    setImages([])
    setImageError(null)
    setView("chat")
    void a.send(text, shots)
  }

  return (
    <aside
      style={{ width }}
      className={cn("relative flex h-full max-w-[70vw] shrink-0 flex-col overflow-hidden rounded-xl border bg-background", resizing && "select-none")}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        title="Drag to resize · double-click to reset"
        onMouseDown={startResize}
        onDoubleClick={resetWidth}
        className={cn(
          "absolute inset-y-0 left-0 z-10 w-1.5 cursor-col-resize transition-colors hover:bg-primary/20",
          resizing && "bg-primary/30",
        )}
      />
      <header className="flex items-center gap-1 border-b px-3 py-2">
        <Sparkles className="h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight">Assistant</p>
        </div>
        <ModelPicker value={a.model} onChange={a.setModel} />
        <Button variant="ghost" size="icon" className="h-7 w-7" title="Past chats" onClick={() => setView(v => v === "history" ? "chat" : "history")}>
          <History className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7" title="New chat" disabled={a.busy} onClick={() => { a.newChat(); setView("chat") }}>
          <Plus className="h-4 w-4" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7" title="More">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onSelect={() => setSkillsOpen(true)} className="gap-2 text-xs">
              <Zap className="h-3.5 w-3.5" /> Skills{a.skills.length > 0 && ` (${a.skills.length})`}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setMemoryOpen(true)} className="gap-2 text-xs">
              <Brain className="h-3.5 w-3.5" /> Memory{a.memory.length > 0 && ` (${a.memory.length})`}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setDocsOpen(true)} className="gap-2 text-xs">
              <FolderUp className="h-3.5 w-3.5" /> Documents{a.corpora.attachedIds.length > 0 && ` (${a.corpora.attachedIds.length} attached)`}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="ghost" size="icon" className="h-7 w-7" title="Close" onClick={() => a.setOpen(false)}>
          <X className="h-4 w-4" />
        </Button>
      </header>

      {view === "history" ? (
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {a.persistence === "missing" && (
            <p className="p-3 text-xs text-muted-foreground">Chats aren't saved yet: the assistant database tables haven't been created.</p>
          )}
          {a.conversations.length === 0 && a.persistence === "ok" && (
            <p className="p-3 text-xs text-muted-foreground">No saved chats yet.</p>
          )}
          <div className="space-y-0.5">
            {a.conversations.map(c => (
              <div key={c.id} className={cn("group flex items-center gap-1 rounded-md px-2 py-1.5 hover:bg-muted", c.id === a.conversationId && "bg-muted")}>
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => { void a.loadConversation(c.id); setView("chat") }}
                >
                  <p className="truncate text-sm">{c.title || "Untitled chat"}</p>
                  <p className="text-[11px] text-muted-foreground">{new Date(c.updatedAt).toLocaleString()}</p>
                </button>
                <Button
                  variant="ghost" size="icon" className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100"
                  title="Delete chat" onClick={() => void a.deleteConversation(c.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
          {a.messages.length === 0 && (
            <div className="space-y-3 pt-6">
              <p className="text-center text-sm text-muted-foreground">
                Ask me to do anything you can do in the app. I can move between pages and I remember our chats.
              </p>
              <div className="flex flex-col gap-1.5">
                {suggestionsFor(moduleIds).map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { a.setDraft(s); inputRef.current?.focus() }}
                    className="rounded-lg border px-3 py-2 text-left text-xs hover:bg-muted"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {a.messages.map(m => (
            <div key={m.id} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
              {m.role === "user" ? (
                <div className="flex max-w-[85%] flex-col items-end gap-1.5">
                  {!!m.images?.length && (
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {m.images.map((src, i) => (
                        <button key={i} type="button" onClick={() => setViewing(src)} title="View screenshot"
                          className="overflow-hidden rounded-lg border bg-muted">
                          <img src={src} alt={`Screenshot ${i + 1}`} className="h-20 max-w-[160px] object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="whitespace-pre-wrap rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-sm text-primary-foreground">
                    {m.content}
                  </div>
                </div>
              ) : (
                <div className="w-full min-w-0 space-y-2">
                  {/* Text and tool calls in the order they happened */}
                  {interleave(m.content, m.tools).map((block, i) => block.kind === "tools" ? (
                    <ToolExecutionList
                      key={`tools-${block.tools[0].id}`}
                      executions={block.tools.map(t => ({ ...t, args: t.args ?? {} }))}
                      expanded={expanded}
                      onToggle={id => setExpanded(prev => {
                        const next = new Set(prev)
                        if (next.has(id)) next.delete(id)
                        else next.add(id)
                        return next
                      })}
                      labelFor={a.labelFor}
                    />
                  ) : (
                    <div key={`text-${i}`} className="text-sm" onClick={onLinkClick}><ToMarkdown>{withAppLinks(block.text)}</ToMarkdown></div>
                  ))}
                  {m.isStreaming && !m.content && !(m.tools?.length) && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {a.approvals.length > 0 && (
        <ApprovalCard approval={a.approvals[0]} onResolve={v => a.resolveApproval(a.approvals[0].id, v)} />
      )}

      <div className="relative border-t p-3">
        {slashMatches.length > 0 && (
          <div className="absolute bottom-full left-3 right-3 mb-1 max-h-48 overflow-y-auto rounded-md border bg-popover p-1 shadow-md">
            {slashMatches.map(s => (
              <button
                key={s.id}
                type="button"
                className="w-full rounded px-2 py-1.5 text-left hover:bg-muted"
                onClick={() => { a.setDraft(`/${s.name} `); inputRef.current?.focus() }}
              >
                <p className="text-xs font-medium">/{s.name}</p>
                {s.description && <p className="truncate text-[11px] text-muted-foreground">{s.description}</p>}
              </button>
            ))}
          </div>
        )}
        {/* One box: the text grows upward, the controls sit along its bottom edge */}
        <div
          className={cn(
            "cursor-text rounded-2xl border bg-background shadow-xs transition-[border-color,box-shadow] focus-within:border-primary/40 focus-within:ring-3 focus-within:ring-primary/10",
            dragging && "border-primary ring-3 ring-primary/20",
          )}
          onClick={e => { if (e.target === e.currentTarget) inputRef.current?.focus() }}
          onDragOver={e => { if ([...e.dataTransfer.items].some(i => i.kind === "file")) { e.preventDefault(); setDragging(true) } }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); void addImages([...e.dataTransfer.files]) }}
        >
          {images.length > 0 && (
            <div className="flex flex-wrap gap-2 px-3 pt-3">
              {images.map((src, i) => (
                <div key={i} className="group relative">
                  <button type="button" onClick={() => setViewing(src)} className="block overflow-hidden rounded-lg border bg-muted" title="View screenshot">
                    <img src={src} alt={`Screenshot ${i + 1}`} className="h-14 w-20 object-cover" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}
                    className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm hover:text-foreground"
                    title="Remove screenshot"
                    aria-label="Remove screenshot"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <textarea
            onPaste={e => {
              const files = [...e.clipboardData.files].filter(f => f.type.startsWith("image/"))
              if (files.length) { e.preventDefault(); void addImages(files) }
            }}
            ref={inputRef}
            id="assistant-input"
            value={a.draft}
            onChange={e => a.setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit() }
            }}
            placeholder={pendingQuestion ? "Type your answer" : slashSkills.length ? "Ask anything, or / for skills" : "Ask anything"}
            rows={1}
            // Browser writing extensions overlay their own buttons on the box
            data-gramm="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
            className="block max-h-40 min-h-[44px] w-full resize-none overflow-y-auto bg-transparent px-3.5 pb-1 pt-3 text-sm leading-relaxed outline-none placeholder:text-muted-foreground"
          />
          <div className="flex items-center gap-2 px-2 pb-2">
            <input
              ref={fileRef} type="file" accept="image/*" multiple className="hidden"
              onChange={e => { void addImages([...(e.target.files ?? [])]); e.target.value = "" }}
            />
            <Button
              type="button" variant="ghost" size="icon"
              className="h-7 w-7 shrink-0 rounded-full text-muted-foreground"
              disabled={images.length >= MAX_IMAGES}
              onClick={() => fileRef.current?.click()}
              title="Attach screenshots (or paste or drop them). They go to the model with this message and aren't saved."
            >
              <ImagePlus className="h-4 w-4" />
            </Button>
            {a.canAutoAccept && (
              <label
                className={cn(
                  "flex cursor-pointer items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] transition-colors",
                  a.autoAccept ? "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300" : "text-muted-foreground hover:bg-muted",
                )}
                title="Apply changes without asking"
              >
                <Switch id="assistant-auto-accept" checked={a.autoAccept} onCheckedChange={a.setAutoAccept} className="scale-[0.65]" />
                Auto-accept
              </label>
            )}
            <span className="hidden text-[10px] text-muted-foreground/70 sm:inline">Shift+Enter for a new line</span>
            <div className="flex-1" />
            {a.busy && !(pendingQuestion && a.draft.trim()) ? (
              <Button size="icon" variant="outline" className="h-8 w-8 shrink-0 rounded-full" title="Stop" onClick={a.stop}>
                <Square className="h-3 w-3 fill-current" />
              </Button>
            ) : (
              <Button
                size="icon"
                className="h-8 w-8 shrink-0 rounded-full disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100"
                title="Send"
                disabled={!a.draft.trim() && !images.length}
                onClick={submit}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        {imageError && <p className="mt-1.5 text-[11px] text-destructive">{imageError}</p>}
        <p className="mt-1.5 text-center text-[10px] text-muted-foreground">
          {a.persistence === "missing"
            ? "This chat isn't being saved yet."
            : `Chats are private to you and kept for ${RETENTION_DAYS} days.`}
        </p>
      </div>

      {viewing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
          onClick={() => setViewing(null)}
          role="dialog"
          aria-label="Screenshot"
        >
          <img src={viewing} alt="Screenshot" className="max-h-full max-w-full rounded-lg shadow-2xl" />
        </div>
      )}

      <SkillsDialog open={skillsOpen} onOpenChange={setSkillsOpen} />
      <MemoryDialog open={memoryOpen} onOpenChange={setMemoryOpen} />
      <CorpusPanel
        open={docsOpen}
        onOpenChange={setDocsOpen}
        corpora={a.corpora.corpora}
        attachedIds={a.corpora.attachedIds}
        ingesting={a.corpora.ingesting}
        onIngest={a.canConfigure ? (files, name) => a.corpora.ingest(files, name) : undefined}
        onRemove={a.canConfigure ? id => a.corpora.remove(id) : undefined}
        onToggleAttached={a.corpora.toggleAttached}
      />
    </aside>
  )
}
